import { createSlice } from "@reduxjs/toolkit";

import { DEFAULT_FOLLOWERS_FOLLOWING_PAGE_SIZE } from "../../../utils";
import { ConnectionsProps } from "./connections";

const defaultAPI_Status: ConnectionsProps.API = {
  getConnections_API_STATUS: "idle",
  followAUser_API_STATUS: "idle",
  unfollowAUser_API_STATUS: "idle",
  getFollowCounts_API_STATUS: "idle",
  getFollowersUsers_API_STATUS: "idle",
  getFollowingUsers_API_STATUS: "idle",
  searchUsers_API_STATUS: "idle",
  removeAFollower_API_STATUS: "idle",
};
const defaultPageProps: ConnectionsProps.PageProps = {
  page: 1,
  pageSize: DEFAULT_FOLLOWERS_FOLLOWING_PAGE_SIZE,
  remainingCount: 0,
  totalCount: 0,
  totalPages: 0,
};
const initialUsers: ConnectionsProps.FollowersUsers_API_RESPONSE = {
  data: [],
  ...defaultPageProps,
};
const commonInitialState: ConnectionsProps.Common = {
  followers_count: 0,
  following_count: 0,
  selectedUserId: "",
  followingIds: {},
};

const initialState: ConnectionsProps.State = {
  ...defaultAPI_Status,
  connections: [],
  followersUsers: initialUsers,
  followingUsers: initialUsers,
  searchUsers: initialUsers,
  ...commonInitialState,
};

const slice = createSlice({
  name: "connections",
  initialState,
  reducers: {
    setFollowCounts: (state, action: ConnectionsProps.setFollowCounts) => {
      const { followers_count = 0, following_count = 0 } = action.payload;
      state.followers_count = followers_count;
      state.following_count = following_count;
    },
    setConnections: (state, action: ConnectionsProps.setConnections) => {
      state.connections = action.payload;
    },
    setFollowersUsers: (state, action: ConnectionsProps.setFollowersUsers) => {
      const { data, page, pageSize, remainingCount, totalCount, totalPages } =
        action.payload;

      state.followersUsers = {
        data: state.followersUsers.data.concat(data),
        page: page + 1,
        pageSize,
        remainingCount,
        totalCount,
        totalPages,
      };
    },
    resetFollowersUsers: (state) => {
      state.followersUsers = initialUsers;
    },
    setFollowingUsers: (state, action: ConnectionsProps.setFollowingUsers) => {
      const { data, page, pageSize, remainingCount, totalCount, totalPages } =
        action.payload;

      state.followingUsers = {
        data: state.followingUsers.data.concat(data),
        page: page + 1,
        pageSize,
        remainingCount,
        totalCount,
        totalPages,
      };
    },
    resetFollowingUsers: (state) => {
      state.followingUsers = initialUsers;
    },
    setSelectedUserId: (state, action: ConnectionsProps.setSelectedUserId) => {
      state.selectedUserId = action.payload;
    },
    addFollowingUser: (state, action: ConnectionsProps.addFollowingUser) => {
      state.followingIds[action.payload] = true;
      state.following_count += 1;
    },
    removeFollowingUserUsingId: (
      state,
      action: ConnectionsProps.removeFollowingUserUsingId
    ) => {
      state.followingUsers.data = state.followingUsers.data.filter(
        (user) => user.id !== action.payload
      );
      delete state.followingIds[action.payload];
      state.following_count -= 1;
    },
    removeFollowerUserUsingId: (
      state,
      action: ConnectionsProps.removeFollowingUserUsingId
    ) => {
      state.followersUsers.data = state.followersUsers.data.filter((user) => {
        return user.id !== action.payload;
      });
      state.followers_count -= 1;
    },
    setSearchUsersResponse: (
      state,
      action: ConnectionsProps.setSearchUsersResponse
    ) => {
      const { data, page, pageSize, remainingCount, totalCount, totalPages } =
        action.payload;
      //Filter out the data that is already in the state for the same type
      state.searchUsers = {
        data: page === 1 ? data : state.searchUsers.data.concat(data),
        page: page + 1,
        pageSize,
        remainingCount,
        totalCount,
        totalPages,
      };
    },
    resetSearchUsers: (state) => {
      state.searchUsers = { data: state.searchUsers.data, ...defaultPageProps };
      state.searchUsers_API_STATUS = "idle";
    },
    resetSearchUsersIncludingData: (state) => {
      state.searchUsers = initialUsers;
      state.searchUsers_API_STATUS = "idle";
    },

    //#region API
    setGetConnections_API_STATUS: (
      state,
      action: ConnectionsProps.setGetConnections_API_STATUS
    ) => {
      state.getConnections_API_STATUS = action.payload;
    },
    setFollowAUser_API_STATUS: (
      state,
      action: ConnectionsProps.setFollowAUser_API_STATUS
    ) => {
      state.followAUser_API_STATUS = action.payload;
    },
    setUnfollowAUser_API_STATUS: (
      state,
      action: ConnectionsProps.setUnfollowAUser_API_STATUS
    ) => {
      state.unfollowAUser_API_STATUS = action.payload;
    },
    setGetFollowCounts_API_STATUS: (
      state,
      action: ConnectionsProps.setGetFollowCounts_API_STATUS
    ) => {
      state.getFollowCounts_API_STATUS = action.payload;
    },
    setGetFollowersUsers_API_STATUS: (
      state,
      action: ConnectionsProps.setGetFollowersUsers_API_STATUS
    ) => {
      state.getFollowersUsers_API_STATUS = action.payload;
    },
    setGetFollowingUsers_API_STATUS: (
      state,
      action: ConnectionsProps.setGetFollowingUsers_API_STATUS
    ) => {
      state.getFollowingUsers_API_STATUS = action.payload;
    },
    setSearchUsers_API_STATUS: (
      state,
      action: ConnectionsProps.setSearchUsers_API_STATUS
    ) => {
      state.searchUsers_API_STATUS = action.payload;
    },
    setRemoveAFollower_API_STATUS: (
      state,
      action: ConnectionsProps.setRemoveAFollower_API_STATUS
    ) => {
      state.removeAFollower_API_STATUS = action.payload;
    },
    //#endregion

    reset: () => initialState,
  },
});

export const connectionsActions = slice.actions;
export const connectionsReducer = slice.reducer;
